import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import { PlayArrow, AudioFile, VideoFile, CheckCircle, PendingActions, Search, Mic } from '@mui/icons-material';
import logger from '../utils/logger';
import { apiGet, apiPost } from '../utils/api';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const POLL_INTERVAL = 5000; // Poll every 5 seconds 

const Gallery = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileType, setFileType] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [processingFiles, setProcessingFiles] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const ITEMS_PER_PAGE = 10;

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams({
        page,
        pageSize: ITEMS_PER_PAGE
      });
      
      if (fileType !== 'all') {
        queryParams.append('type', fileType);
      }

      const data = await apiGet(`/files?${queryParams}`);
      
      setFiles(data.files);
      setTotalPages(data.totalPages);
      
      // Update processing files set
      const newProcessingFiles = new Set(
        data.files
          .filter(file => file.metadata?.transcribeStatus === 'processing')
          .map(file => file.key)
      );
      setProcessingFiles(newProcessingFiles);
    } catch (error) {
      logger.error('Error fetching files:', error);
      setError('Failed to fetch files. Please try again later.');
      showSnackbar('Failed to fetch files', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (event) => {
    const newType = event.target.value;
    setFileType(newType);
    setPage(1);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const checkFileStatus = useCallback(async (fileKey) => {
    try {
      const data = await apiGet(`/files/${encodeURIComponent(fileKey)}/status`);
      
      // Update file metadata with new status
      setFiles(prev => prev.map(f => 
        f.key === fileKey
          ? { ...f, metadata: { ...f.metadata, ...data } }
          : f
      ));

      // Handle completion states
      if (data.transcribeStatus === 'completed' || data.transcribeStatus === 'failed') {
        // Remove from processing set
        setProcessingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(fileKey);
          return newSet;
        });
        
        // Show appropriate notification
        showSnackbar(
          data.transcribeStatus === 'completed' 
            ? 'Transcription completed successfully'
            : 'Transcription failed',
          data.transcribeStatus === 'completed' ? 'success' : 'error'
        );
      }
    } catch (error) {
      logger.error('Error checking file status:', { error, fileKey });
      showSnackbar('Failed to check transcription status', 'error');
    }
  }, []);

  const handleStartTranscription = async (fileKey) => {
    try {
      // Update UI optimistically
      setFiles(prev => prev.map(f => 
        f.key === fileKey
          ? { ...f, metadata: { ...f.metadata, transcribeStatus: 'processing' } }
          : f
      ));

      // Make API call
      await apiPost('/transcribe', { fileKey });
      
      // Update processing state
      setProcessingFiles(prev => {
        const newSet = new Set(prev);
        newSet.add(fileKey);
        return newSet;
      });

      showSnackbar('Transcription started successfully', 'success');
    } catch (error) {
      // Revert UI state on error
      setFiles(prev => prev.map(f => 
        f.key === fileKey
          ? { ...f, metadata: { ...f.metadata, transcribeStatus: 'failed' } }
          : f
      ));
      logger.error('Error starting transcription:', error);
      showSnackbar(error.message || 'Failed to start transcription', 'error');
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [page, fileType]);

  useEffect(() => {
    if (processingFiles.size === 0) return;

    const intervalId = setInterval(() => {
      processingFiles.forEach(fileKey => {
        checkFileStatus(fileKey);
      });
    }, POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [processingFiles, checkFileStatus]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    try {
      // Ensure we have a valid date string
      if (!dateString) {
        return 'Unknown Date';
      }
      
      // Try to parse the date string
      const timestamp = Date.parse(dateString);
      if (isNaN(timestamp)) {
        return 'Invalid Date';
      }

      const date = new Date(timestamp);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'UTC'
      }).format(date);
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusChip = (status) => {
    const colors = {
      completed: 'success',
      'not-started': 'default',
      pending: 'warning',
      failed: 'error',
      processing: 'info'
    };

    const icons = {
      completed: <CheckCircle fontSize="small" />,
      'not-started': null,
      pending: <PendingActions fontSize="small" />,
      failed: null,
      processing: <CircularProgress size={16} />
    };

    return (
      <Chip
        size="small"
        color={colors[status] || 'default'}
        icon={icons[status]}
        label={status}
      />
    );
  };

  if (error) {
    return (
      <Box sx={{ p: 3, color: 'error.main' }}>
        Error: {error}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h2">
          Knowledge Base Gallery
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>File Type</InputLabel>
            <Select value={fileType} onChange={handleTypeChange} label="File Type">
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="audio">Audio</MenuItem>
              <MenuItem value="video">Video</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Filename</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Upload Date</TableCell>
                  <TableCell>Uploaded By</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Transcription Status</TableCell>
                  <TableCell>Actions</TableCell>
                  <TableCell>Searchable</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.key} hover>
                    <TableCell>{file.filename}</TableCell>
                    <TableCell>
                      {file.type === 'audio' ? (
                        <AudioFile color="primary" />
                      ) : (
                        <VideoFile color="secondary" />
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(file.metadata.uploadTimestamp)}
                    </TableCell>
                    <TableCell>
                      {file.metadata.uploadedBy || 'Unknown'}
                    </TableCell>
                    <TableCell>{formatFileSize(file.size)}</TableCell>
                    <TableCell>{getStatusChip(file.metadata.transcribeStatus)}</TableCell>
                    <TableCell>
                      <Tooltip title={
                        file.metadata.transcribeStatus === 'not-started'
                          ? 'Start transcription'
                          : file.metadata.transcribeStatus === 'processing'
                          ? 'Transcription in progress'
                          : file.metadata.transcribeStatus === 'completed'
                          ? 'Transcription completed'
                          : 'Transcription failed'
                      }>
                        <span>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={file.metadata.transcribeStatus === 'processing' ? <CircularProgress size={16} /> : <Mic />}
                            onClick={() => handleStartTranscription(file.key)}
                            disabled={file.metadata.transcribeStatus !== 'not-started'}
                            color={
                              file.metadata.transcribeStatus === 'completed' 
                                ? 'success'
                                : file.metadata.transcribeStatus === 'failed'
                                ? 'error'
                                : 'primary'
                            }
                          >
                            {file.metadata.transcribeStatus === 'processing'
                              ? 'In Progress'
                              : file.metadata.transcribeStatus === 'not-started'
                              ? 'Transcribe'
                              : file.metadata.transcribeStatus === 'completed'
                              ? 'Done'
                              : 'Failed'
                            }
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={<Search fontSize="small" />}
                        color={file.metadata.searchable ? "success" : "default"}
                        label={file.metadata.searchable ? "Yes" : "No"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Gallery;
